import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateSiteSettings } from '@/hooks/useSiteSettings';
import { LogOut, Users, FileText, Inbox, BarChart3, Plus, Pencil, Trash2, Check, X, Eye, ChevronDown, ChevronUp, FileCheck, Bell, Mail, MailX, BookOpen, UserCog, Shield, Download, TrendingUp, Monitor, Smartphone, ArrowLeft, Search, ChevronLeft, ChevronRight, Newspaper, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable image-upload field. Accepts an existing URL or lets the user upload
 * a fresh file (stored in MongoDB via /api/admin/upload/image, redeploy-safe).
 *
 * Props:
 *   label      — field label shown above the input
 *   value      — current URL string
 *   onChange   — (newUrl: string) => void
 *   placeholder, testid (optional), previewClass (optional Tailwind class for the preview <img>)
 */
function ImageUploadField({ label, value, onChange, placeholder = 'https://… or click Upload', testid = 'image-upload', previewClass = 'mt-3 max-h-32 object-cover border border-[#A0743A]/20' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is larger than 5 MB. Please compress and try again.');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/admin/upload/image`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Upload failed.');
      } else {
        const data = await res.json();
        onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
    } catch {
      alert('Network error during upload.');
    }
    setUploading(false);
  };

  return (
    <div>
      <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">{label}</label>
      <div className="flex gap-2">
        <input data-testid={`${testid}-url`} value={value || ''} onChange={e => onChange(e.target.value)} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none flex-1" placeholder={placeholder} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="border border-[#A0743A]/40 text-[#A0743A] px-3 py-1 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors disabled:opacity-50 whitespace-nowrap" data-testid={`${testid}-upload`}>
          {uploading ? '…' : 'Upload'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testid}-input`} />
      </div>
      {value && (
        <img src={value} alt="Preview" className={previewClass} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-[#A0743A]/20 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon size={20} className="text-[#A0743A] mb-3" strokeWidth={1.25} />
      <p className="text-2xl text-[#FAF8F5] font-semibold mb-1" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>{value}</p>
      <p className="text-[#999] text-xs uppercase tracking-wider">{label}</p>
      {sub && <p className="text-[#C9A96E] text-xs mt-1">{sub}</p>}
    </div>
  );
}

function DealForm({ deal, onSave, onCancel }) {
  const [form, setForm] = useState(deal || {
    deal_name: '', deal_code: '', asset_class: 'Private Equity', deal_type: 'Equity',
    target_return: '', deal_size: '', min_ticket_size: '', tenor: '',
    security_structure: '', sector: '', geography: '', originator_credentials: '',
    deal_status: 'Open', sawai_rating: 'A', im_available: 'Pending NDA', description: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">{label}</label>
      <input
        data-testid={`deal-form-${key}`}
        type={type}
        value={form[key] || ''}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full"
        placeholder={placeholder}
      />
    </div>
  );

  const selectField = (label, key, options) => (
    <div>
      <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">{label}</label>
      <select
        data-testid={`deal-form-${key}`}
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="border border-[#A0743A]/20 p-8 space-y-6" data-testid="deal-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {field('Deal Name *', 'deal_name', 'text', 'Project Evergreen')}
        {field('Deal Code', 'deal_code', 'text', 'SC-PE-2026-001')}
        {selectField('Asset Class', 'asset_class', ['Private Equity', 'Venture Capital', 'Private Debt', 'Venture Debt'])}
        {selectField('Deal Type', 'deal_type', ['Senior Secured', 'Mezzanine', 'Equity', 'Co-Investment', 'Fund Commitment'])}
        {field('Target Return *', 'target_return', 'text', '18-22% p.a.')}
        {field('Deal Size *', 'deal_size', 'text', 'INR 100 Crore')}
        {field('Min Ticket Size', 'min_ticket_size', 'text', 'INR 5 Crore')}
        {field('Tenor *', 'tenor', 'text', '3-5 Years')}
        {field('Sector *', 'sector', 'text', 'Healthcare')}
        {field('Geography', 'geography', 'text', 'Mumbai, Maharashtra')}
        {selectField('Status', 'deal_status', ['Open', 'Partially Subscribed', 'Closing Soon', 'Closed'])}
        {selectField('Sawai Rating', 'sawai_rating', ['A+', 'A', 'B+', 'B'])}
        {selectField('IM Available', 'im_available', ['Yes', 'Pending NDA', 'Coming Soon'])}
      </div>
      {field('Security / Structure', 'security_structure', 'text', 'First-charge mortgage...')}
      {field('Originator Credentials', 'originator_credentials', 'text', 'NBFC with INR 2,500Cr AUM...')}
      <div>
        <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Description</label>
        <textarea
          data-testid="deal-form-description"
          value={form.description || ''}
          onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          rows={4}
          className="bg-transparent border border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] p-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none"
          placeholder="Deal overview..."
        />
      </div>
      <div className="flex gap-4">
        <button type="submit" disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="deal-form-submit">
          {saving ? 'Saving...' : (deal ? 'Update Deal' : 'Create Deal')}
        </button>
        <button type="button" onClick={onCancel} className="border border-[#A0743A]/40 text-[#A0743A] px-8 py-3 text-sm uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors" data-testid="deal-form-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}

// Tab components
function DealsTab() {
  const [deals, setDeals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/deals`, { credentials: 'include' });
      if (res.ok) setDeals(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleSave = async (form) => {
    if (editDeal) {
      await fetch(`${API}/deals/${editDeal.deal_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(form)
      });
    } else {
      await fetch(`${API}/deals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(form)
      });
    }
    setShowForm(false);
    setEditDeal(null);
    fetchDeals();
  };

  const handleDelete = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;
    await fetch(`${API}/deals/${dealId}`, { method: 'DELETE', credentials: 'include' });
    fetchDeals();
  };

  if (showForm || editDeal) {
    return (
      <div>
        <h3 className="text-xl text-[#FAF8F5] mb-6">{editDeal ? 'Edit Deal' : 'New Deal'}</h3>
        <DealForm
          deal={editDeal}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditDeal(null); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Manage Deals ({deals.length})</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-5 py-2.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="add-deal-btn">
          <Plus size={14} /> Add Deal
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-deals-list">
          {deals.map(deal => (
            <div key={deal.deal_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-[#A0743A]/40 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-[#A0743A] text-xs uppercase tracking-wider">{deal.asset_class}</span>
                  <span className="text-[#555] text-xs">{deal.deal_status}</span>
                  <span className="text-[#C9A96E] text-xs">{deal.sawai_rating}</span>
                </div>
                <p className="text-[#FAF8F5]">{deal.deal_name}</p>
                <p className="text-[#999] text-xs">{deal.target_return} &middot; {deal.deal_size}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditDeal(deal)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors" data-testid={`edit-deal-${deal.deal_id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => handleDelete(deal.deal_id)} className="border border-red-500/30 text-red-400 p-2 hover:bg-red-500/10 transition-colors" data-testid={`delete-deal-${deal.deal_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvestorsTab() {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQ, setSearchQ] = useState('');

  const fetchInvestors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (searchQ.trim()) params.set('search', searchQ.trim());
      const res = await fetch(`${API}/admin/investors/list/paginated?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setInvestors(data.items);
        setTotalPages(data.pages);
        setTotal(data.total);
      }
    } catch {}
    setLoading(false);
  }, [page, searchQ]);

  useEffect(() => { fetchInvestors(); }, [fetchInvestors]);

  const updateStatus = async (userId, status) => {
    const res = await fetch(`${API}/investors/${userId}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ status })
    });
    if (res.ok) {
      const updated = await res.json();
      setInvestors(prev => prev.map(i => i.user_id === userId ? updated : i));
    }
  };

  const statusBadge = (status) => {
    const colors = {
      approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
      rejected: 'bg-red-500/15 text-red-400 border-red-500/30',
    };
    return `px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border ${colors[status] || 'text-[#999]'}`;
  };

  if (selectedUserId) {
    return <InvestorCRMView userId={selectedUserId} onBack={() => setSelectedUserId(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl text-[#FAF8F5]">Investors ({total})</h3>
      </div>
      <div className="mb-6 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0743A]" />
        <input data-testid="investor-search" value={searchQ} onChange={e => { setSearchQ(e.target.value); setPage(1); }} placeholder="Search by name, email, entity..." className="w-full bg-transparent border border-[#A0743A]/30 text-[#FAF8F5] pl-9 pr-4 py-2.5 placeholder-[#555] focus:border-[#C9A96E] focus:outline-none text-sm" />
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          <div className="space-y-3" data-testid="admin-investors-list">
            {investors.map(inv => (
              <div key={inv.user_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-[#A0743A]/40 transition-colors">
                <div className="flex-1 cursor-pointer" onClick={() => setSelectedUserId(inv.user_id)}>
                  <div className="flex items-center gap-3 mb-1">
                    <span className={statusBadge(inv.status)}>{inv.status}</span>
                    <span className="text-[#C9A96E] text-xs uppercase tracking-wider">{inv.role}</span>
                  </div>
                  <p className="text-[#FAF8F5]">{inv.name}</p>
                  <p className="text-[#999] text-xs">{inv.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedUserId(inv.user_id)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors" data-testid={`view-investor-${inv.user_id}`} title="View full profile"><Eye size={14} /></button>
                  {inv.status !== 'approved' && (
                    <button onClick={() => updateStatus(inv.user_id, 'approved')} className="flex items-center gap-1 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 text-xs hover:bg-emerald-500/10 transition-colors" data-testid={`approve-investor-${inv.user_id}`}><Check size={12} /> Approve</button>
                  )}
                  {inv.status !== 'rejected' && (
                    <button onClick={() => updateStatus(inv.user_id, 'rejected')} className="flex items-center gap-1 border border-red-500/30 text-red-400 px-3 py-1.5 text-xs hover:bg-red-500/10 transition-colors" data-testid={`reject-investor-${inv.user_id}`}><X size={12} /> Reject</button>
                  )}
                </div>
              </div>
            ))}
            {investors.length === 0 && <p className="text-[#999] text-center py-8">No investors found.</p>}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6" data-testid="pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 disabled:opacity-30 transition-colors"><ChevronLeft size={14} /></button>
              <span className="text-[#999] text-xs">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 disabled:opacity-30 transition-colors"><ChevronRight size={14} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IMRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/im-requests`, { credentials: 'include' });
        if (res.ok) setRequests(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  const updateStatus = async (reqId, status) => {
    const res = await fetch(`${API}/im-requests/${reqId}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ status })
    });
    if (res.ok) {
      const updated = await res.json();
      setRequests(prev => prev.map(r => r.request_id === reqId ? updated : r));
    }
  };

  return (
    <div>
      <h3 className="text-xl text-[#FAF8F5] mb-6">IM Requests ({requests.length})</h3>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-im-requests-list">
          {requests.map(req => (
            <div key={req.request_id} className="border border-[#A0743A]/15 p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border ${
                      req.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                      req.status === 'rejected' ? 'bg-red-500/15 text-red-400 border-red-500/30' :
                      'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    }`}>{req.status}</span>
                    <span className="text-[#999] text-xs">Deal: {req.deal_id}</span>
                  </div>
                  <p className="text-[#FAF8F5]">{req.user_name}</p>
                  <p className="text-[#999] text-xs">{req.user_email}</p>
                  {req.message && <p className="text-[#555] text-sm mt-2 italic">"{req.message}"</p>}
                </div>
                <div className="flex items-center gap-2">
                  {req.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(req.request_id, 'approved')} className="flex items-center gap-1 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 text-xs hover:bg-emerald-500/10 transition-colors" data-testid={`approve-im-${req.request_id}`}>
                        <Check size={12} /> Approve
                      </button>
                      <button onClick={() => updateStatus(req.request_id, 'rejected')} className="flex items-center gap-1 border border-red-500/30 text-red-400 px-3 py-1.5 text-xs hover:bg-red-500/10 transition-colors" data-testid={`reject-im-${req.request_id}`}>
                        <X size={12} /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {requests.length === 0 && <p className="text-[#999] text-center py-8">No IM requests yet.</p>}
        </div>
      )}
    </div>
  );
}

function AccessRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/access-requests`, { credentials: 'include' });
        if (res.ok) setRequests(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  const updateStatus = async (reqId, status) => {
    const res = await fetch(`${API}/access-requests/${reqId}/status`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ status })
    });
    if (res.ok) {
      const updated = await res.json();
      setRequests(prev => prev.map(r => r.request_id === reqId ? updated : r));
    }
  };

  return (
    <div>
      <h3 className="text-xl text-[#FAF8F5] mb-6">Access Requests ({requests.length})</h3>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-access-requests-list">
          {requests.map(req => (
            <div key={req.request_id} className="border border-[#A0743A]/15 p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border ${
                      req.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                      req.status === 'rejected' ? 'bg-red-500/15 text-red-400 border-red-500/30' :
                      'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    }`}>{req.status}</span>
                    {req.entity_type && <span className="text-[#C9A96E] text-xs">{req.entity_type}</span>}
                  </div>
                  <p className="text-[#FAF8F5]">{req.name}</p>
                  <p className="text-[#999] text-xs">{req.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpanded(expanded === req.request_id ? null : req.request_id)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors">
                    {expanded === req.request_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {req.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(req.request_id, 'approved')} className="flex items-center gap-1 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 text-xs hover:bg-emerald-500/10 transition-colors" data-testid={`approve-access-${req.request_id}`}>
                        <Check size={12} /> Approve
                      </button>
                      <button onClick={() => updateStatus(req.request_id, 'rejected')} className="flex items-center gap-1 border border-red-500/30 text-red-400 px-3 py-1.5 text-xs hover:bg-red-500/10 transition-colors" data-testid={`reject-access-${req.request_id}`}>
                        <X size={12} /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
              {expanded === req.request_id && (
                <div className="mt-4 pt-4 border-t border-[#A0743A]/10 grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-[#999] text-xs">Entity:</span> <span className="text-[#FAF8F5] ml-2">{req.entity_name || '-'}</span></div>
                  <div><span className="text-[#999] text-xs">AUM Range:</span> <span className="text-[#FAF8F5] ml-2">{req.aum_range || '-'}</span></div>
                  <div><span className="text-[#999] text-xs">Asset Interest:</span> <span className="text-[#FAF8F5] ml-2">{req.asset_class_interest?.join(', ') || '-'}</span></div>
                  {req.message && <div className="col-span-2"><span className="text-[#999] text-xs">Message:</span> <span className="text-[#FAF8F5] ml-2 italic">"{req.message}"</span></div>}
                </div>
              )}
            </div>
          ))}
          {requests.length === 0 && <p className="text-[#999] text-center py-8">No access requests yet.</p>}
        </div>
      )}
    </div>
  );
}

function NDAsTab() {
  const [ndas, setNdas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/ndas`, { credentials: 'include' });
        if (res.ok) setNdas(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  return (
    <div>
      <h3 className="text-xl text-[#FAF8F5] mb-6">Signed NDAs ({ndas.length})</h3>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-ndas-list">
          {ndas.map(nda => (
            <div key={nda.nda_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    <FileCheck size={10} /> Signed
                  </span>
                  <span className="text-[#999] text-xs">Deal: {nda.deal_id}</span>
                </div>
                <p className="text-[#FAF8F5]">{nda.user_name}</p>
                <p className="text-[#999] text-xs">{nda.user_email}</p>
              </div>
              <div className="text-right">
                <p className="text-[#C9A96E] text-xs">
                  {nda.signed_at ? new Date(nda.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </p>
                {nda.ip_address && <p className="text-[#555] text-[10px] mt-1">IP: {nda.ip_address}</p>}
              </div>
            </div>
          ))}
          {ndas.length === 0 && <p className="text-[#999] text-center py-8">No NDAs signed yet.</p>}
        </div>
      )}
    </div>
  );
}

function ActivityTab() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/admin/notifications`, { credentials: 'include' });
        if (res.ok) setNotifications(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  const eventIcons = { nda_signed: FileCheck, im_request: Inbox };
  const eventColors = { nda_signed: 'text-emerald-400', im_request: 'text-[#A0743A]' };

  return (
    <div>
      <h3 className="text-xl text-[#FAF8F5] mb-6">Activity Feed ({notifications.length})</h3>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : notifications.length === 0 ? (
        <p className="text-[#999] text-center py-8">No activity yet. Events will appear here when investors sign NDAs or request IMs.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-activity-list">
          {notifications.map(n => {
            const Icon = eventIcons[n.event_type] || Bell;
            const color = eventColors[n.event_type] || 'text-[#999]';
            return (
              <div key={n.notification_id} className="border border-[#A0743A]/15 p-5 flex items-start gap-4">
                <Icon size={18} className={`${color} mt-0.5 shrink-0`} strokeWidth={1.25} />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-[#FAF8F5] text-sm font-semibold">{n.title}</p>
                    {n.email_sent ? (
                      <span className="flex items-center gap-1 text-emerald-400 text-[10px]"><Mail size={10} /> Email sent</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[#555] text-[10px]"><MailX size={10} /> No email</span>
                    )}
                  </div>
                  <p className="text-[#999] text-xs">{n.details}</p>
                  <p className="text-[#555] text-[10px] mt-1">
                    {n.created_at ? new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/blog/posts`, { credentials: 'include' });
      if (res.ok) setPosts(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSave = async (form) => {
    if (editPost) {
      await fetch(`${API}/admin/blog/posts/${editPost.post_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    } else {
      await fetch(`${API}/admin/blog/posts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    }
    setShowForm(false);
    setEditPost(null);
    fetchPosts();
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    await fetch(`${API}/admin/blog/posts/${postId}`, { method: 'DELETE', credentials: 'include' });
    fetchPosts();
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/admin/blog/import/csv`, { method: 'POST', credentials: 'include', body: formData });
      if (res.ok) {
        const data = await res.json();
        setImportResult(data);
        fetchPosts();
      } else {
        setImportResult({ error: 'Import failed. Check CSV format.' });
      }
    } catch { setImportResult({ error: 'Upload failed.' }); }
    setImporting(false);
    e.target.value = '';
  };

  const handleSanitizeAll = async () => {
    if (!window.confirm('This will strip inline color/font styles from ALL existing blog posts so text is visible on the dark theme. Safe to run multiple times. Continue?')) return;
    try {
      const res = await fetch(`${API}/admin/blog/sanitize-all`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        alert(`Done! Scanned ${data.total_posts} posts, cleaned ${data.cleaned}.`);
        fetchPosts();
      } else {
        alert('Failed to sanitize posts.');
      }
    } catch {
      alert('Network error while sanitizing posts.');
    }
  };

  const handleNormalizeSlugs = async () => {
    if (!window.confirm('This will clean up any malformed slugs (leading spaces, /blog/ prefixes, encoded characters) on ALL existing posts so their links open correctly. Safe to run multiple times. Continue?')) return;
    try {
      const res = await fetch(`${API}/admin/blog/normalize-slugs`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const details = (data.details || []).map(d => `• ${d.title}: "${d.old}" → "${d.new}"`).join('\n');
        alert(`Done! Scanned ${data.total_posts} posts, fixed ${data.fixed}.${details ? '\n\n' + details : ''}`);
        fetchPosts();
      } else {
        alert('Failed to normalize slugs.');
      }
    } catch {
      alert('Network error while normalizing slugs.');
    }
  };

  if (showForm || editPost) {
    return <BlogForm post={editPost} onSave={handleSave} onCancel={() => { setShowForm(false); setEditPost(null); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Blog Posts ({posts.length})</h3>
        <div className="flex items-center gap-3">
          <button onClick={handleNormalizeSlugs} className="flex items-center gap-2 border border-[#A0743A]/40 text-[#A0743A] px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors" title="Clean up malformed slugs (leading spaces, /blog/ prefixes, etc.) so post links open correctly" data-testid="normalize-slugs-btn">Fix Links</button>
          <button onClick={handleSanitizeAll} className="flex items-center gap-2 border border-[#A0743A]/40 text-[#A0743A] px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors" title="Strip inline colors/classes from existing posts so text is visible on the dark theme" data-testid="sanitize-all-posts-btn">Fix Colors</button>
          <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-2 border border-[#A0743A]/40 text-[#A0743A] px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors" data-testid="import-blog-toggle"><Download size={14} /> Import</button>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-5 py-2.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="add-blog-post-btn"><Plus size={14} /> New Post</button>
        </div>
      </div>

      {/* Import Panel */}
      {showImport && (
        <div className="border border-[#A0743A]/20 p-6 mb-6 space-y-4" data-testid="blog-import-panel">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider font-semibold">Bulk Import Blog Posts</h4>
          <p className="text-[#999] text-sm leading-relaxed">Upload a CSV file to import multiple blog posts at once. Supports exports from Wix, WordPress, and custom CSV files.</p>

          <div className="bg-[#FAF8F5]/5 border border-[#A0743A]/15 p-4">
            <p className="text-[#A0743A] text-xs uppercase tracking-wider font-semibold mb-2">CSV Column Guide</p>
            <p className="text-[#999] text-xs leading-relaxed">
              Your CSV should include these columns (case-flexible):<br />
              <span className="text-[#FAF8F5]">title</span> (required) &middot;
              <span className="text-[#FAF8F5]">content</span> (HTML or plain text) &middot;
              <span className="text-[#FAF8F5]">excerpt</span> &middot;
              <span className="text-[#FAF8F5]">tags</span> (comma-separated) &middot;
              <span className="text-[#FAF8F5]">published_date</span> &middot;
              <span className="text-[#FAF8F5]">cover_image</span> (URL) &middot;
              <span className="text-[#FAF8F5]">status</span> (published/draft)
            </p>
            <p className="text-[#555] text-[10px] mt-2">Also accepts Wix column names: Post Title, Post Body, Featured Image, Categories, Publish Date</p>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors cursor-pointer" data-testid="csv-upload-btn">
              <Download size={14} /> {importing ? 'Importing...' : 'Upload CSV File'}
              <input type="file" accept=".csv,.txt" onChange={handleCSVImport} className="hidden" disabled={importing} />
            </label>
          </div>

          {importResult && (
            <div className={`p-4 border ${importResult.error ? 'border-red-500/30 bg-red-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`} data-testid="import-result">
              {importResult.error ? (
                <p className="text-red-400 text-sm">{importResult.error}</p>
              ) : (
                <div>
                  <p className="text-emerald-400 text-sm font-semibold mb-2">{importResult.imported} post{importResult.imported !== 1 ? 's' : ''} imported successfully!</p>
                  {importResult.skipped > 0 && <p className="text-amber-400 text-xs">{importResult.skipped} rows skipped (missing title)</p>}
                  <div className="mt-2 space-y-1">
                    {importResult.posts?.slice(0, 5).map((p, i) => (
                      <p key={i} className="text-[#999] text-xs">+ {p.title} <span className="text-[#555]">({p.status})</span></p>
                    ))}
                    {importResult.posts?.length > 5 && <p className="text-[#555] text-xs">...and {importResult.posts.length - 5} more</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-blog-list">
          {posts.map(post => (
            <div key={post.post_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-[#A0743A]/40 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border ${post.status === 'published' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'}`}>{post.status}</span>
                  {post.tags?.slice(0, 2).map(t => <span key={t} className="text-[#555] text-[10px]">#{t}</span>)}
                </div>
                <p className="text-[#FAF8F5]">{post.title}</p>
                <p className="text-[#999] text-xs">{post.slug} &middot; {post.author_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditPost(post)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors" data-testid={`edit-post-${post.post_id}`}><Pencil size={14} /></button>
                <button onClick={() => handleDelete(post.post_id)} className="border border-red-500/30 text-red-400 p-2 hover:bg-red-500/10 transition-colors" data-testid={`delete-post-${post.post_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <p className="text-[#999] text-center py-8">No blog posts yet. Click "New Post" to create your first article.</p>}
        </div>
      )}
    </div>
  );
}

function BlogForm({ post, onSave, onCancel }) {
  // Convert ISO published_at -> YYYY-MM-DD for the date input.
  const isoToDateInput = (iso) => {
    if (!iso) return '';
    return typeof iso === 'string' ? iso.slice(0, 10) : '';
  };
  const [form, setForm] = useState(post ? {
    ...post,
    published_date: isoToDateInput(post.published_at)
  } : {
    title: '', slug: '', excerpt: '', content: '', cover_image: '', tags: [], seo_title: '', seo_description: '', status: 'draft', published_date: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && form.content && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = form.content;
    }
  }, [form.content]);

  const execCommand = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    syncContent();
  };

  const syncContent = () => {
    if (editorRef.current) {
      setForm(p => ({ ...p, content: editorRef.current.innerHTML }));
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) execCommand('createLink', url);
  };

  // Strip inline color/background/font styles on paste so pasted Word/Docs content doesn't appear invisible on the published dark-themed blog page.
  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    if (!html) {
      document.execCommand('insertText', false, text);
      syncContent();
      return;
    }

    // Parse and sanitize
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Remove unsupported tags entirely
    tmp.querySelectorAll('style, script, meta, link, title, head').forEach(n => n.remove());

    // Strip inline color/background/font-family/font-size styles from every element
    tmp.querySelectorAll('*').forEach(el => {
      // Remove class + style attributes that carry visual formatting from Word/Docs
      el.removeAttribute('class');
      el.removeAttribute('lang');
      el.removeAttribute('dir');
      if (el.style) {
        el.style.color = '';
        el.style.backgroundColor = '';
        el.style.background = '';
        el.style.fontFamily = '';
        el.style.fontSize = '';
        el.style.lineHeight = '';
        // If style is empty after cleaning, remove the attribute entirely
        if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
      }
    });

    document.execCommand('insertHTML', false, tmp.innerHTML);
    syncContent();
  };

  // One-click "clean existing content" — strips inline colors from content already in the editor (useful for previously-pasted posts)
  const cleanContent = () => {
    if (!editorRef.current) return;
    editorRef.current.querySelectorAll('*').forEach(el => {
      el.removeAttribute('class');
      el.removeAttribute('lang');
      if (el.style) {
        el.style.color = '';
        el.style.backgroundColor = '';
        el.style.background = '';
        el.style.fontFamily = '';
        el.style.fontSize = '';
        if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
      }
    });
    syncContent();
  };

  // ── Image upload helpers ─────────────────────────────────────────
  const inlineImageInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is larger than 5 MB. Please compress it and try again.');
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/admin/upload/image`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || 'Upload failed.');
      return null;
    }
    const data = await res.json();
    // Backend returns a relative URL starting with /api/... — prefix with host so <img> loads through public ingress.
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  };

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const url = await uploadImage(file);
    setUploading(false);
    if (url && editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertImage', false, url);
      // Ensure inserted <img> has sensible styles
      editorRef.current.querySelectorAll('img').forEach(img => {
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      });
      syncContent();
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const url = await uploadImage(file);
    setUploading(false);
    if (url) setForm(p => ({ ...p, cover_image: url }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    syncContent();
    setSaving(true);
    const slug = form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    await onSave({ ...form, slug, content: editorRef.current?.innerHTML || form.content });
    setSaving(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(p => ({ ...p, tags: [...p.tags, tagInput.trim().toLowerCase().replace(/\s+/g, '-')] }));
      setTagInput('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="blog-form">
      <h3 className="text-xl text-[#FAF8F5] mb-4">{post ? 'Edit Post' : 'New Blog Post'}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Title *</label>
          <input data-testid="blog-form-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Article title" required />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">URL Slug</label>
          <input data-testid="blog-form-slug" value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="auto-generated-from-title" />
        </div>
      </div>

      <div>
        <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Excerpt</label>
        <textarea data-testid="blog-form-excerpt" value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} rows={2} className="bg-transparent border border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] p-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none" placeholder="Short summary for previews and SEO..." />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm tracking-wide text-[#A0743A]">Content *</label>
          <button type="button" onClick={() => setShowPreview(!showPreview)} className="text-[#C9A96E] text-xs uppercase tracking-wider hover:text-[#A0743A] transition-colors">{showPreview ? 'Edit' : 'Preview'}</button>
        </div>
        {showPreview ? (
          <div className="bg-[#FAF8F5] p-6 min-h-[300px] text-[#1A1A1A] prose max-w-none" data-testid="blog-form-preview" dangerouslySetInnerHTML={{ __html: form.content }} />
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap gap-1 p-2 border border-[#A0743A]/30 border-b-0 bg-[#1A1A1A]" data-testid="blog-editor-toolbar">
              <button type="button" onClick={() => execCommand('bold')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-sm font-bold" title="Bold">B</button>
              <button type="button" onClick={() => execCommand('italic')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-sm italic" title="Italic">I</button>
              <button type="button" onClick={() => execCommand('underline')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-sm underline" title="Underline">U</button>
              <span className="w-[1px] bg-[#A0743A]/20 mx-1" />
              <button type="button" onClick={() => execCommand('formatBlock', 'h2')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs font-bold" title="Heading 2">H2</button>
              <button type="button" onClick={() => execCommand('formatBlock', 'h3')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs font-bold" title="Heading 3">H3</button>
              <button type="button" onClick={() => execCommand('formatBlock', 'p')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs" title="Paragraph">P</button>
              <span className="w-[1px] bg-[#A0743A]/20 mx-1" />
              <button type="button" onClick={() => execCommand('insertUnorderedList')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs" title="Bullet List">&bull; List</button>
              <button type="button" onClick={() => execCommand('insertOrderedList')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs" title="Numbered List">1. List</button>
              <button type="button" onClick={() => execCommand('formatBlock', 'blockquote')} className="px-3 py-1.5 text-[#FAF8F5] hover:bg-[#A0743A]/20 text-xs italic" title="Blockquote">&ldquo;Quote</button>
              <button type="button" onClick={insertLink} className="px-3 py-1.5 text-[#A0743A] hover:bg-[#A0743A]/20 text-xs" title="Insert Link">Link</button>
              <button type="button" onClick={() => inlineImageInputRef.current?.click()} disabled={uploading} className="px-3 py-1.5 text-[#A0743A] hover:bg-[#A0743A]/20 text-xs disabled:opacity-50" title="Upload an image (JPEG, PNG, WebP, GIF, SVG — up to 5 MB). Inserted at cursor position." data-testid="blog-editor-insert-image">
                {uploading ? 'Uploading…' : 'Image'}
              </button>
              <input ref={inlineImageInputRef} type="file" accept="image/*" onChange={handleInlineImageUpload} className="hidden" data-testid="blog-editor-image-input" />
              <span className="w-[1px] bg-[#A0743A]/20 mx-1" />
              <button type="button" onClick={cleanContent} className="px-3 py-1.5 text-[#A0743A] hover:bg-[#A0743A]/20 text-xs" title="Strip inline colors and classes from pasted content so text is visible on the published dark blog page" data-testid="blog-editor-clean">Clean</button>
            </div>
            {/* Editor */}
            <div
              ref={editorRef}
              contentEditable
              data-testid="blog-form-editor"
              onInput={syncContent}
              onPaste={handlePaste}
              className="bg-[#FAF8F5] text-[#1A1A1A] p-4 min-h-[300px] border border-[#A0743A]/30 focus:outline-none prose max-w-none"
              style={{ fontFamily: "'Jost', sans-serif" }}
              suppressContentEditableWarning
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Cover Image</label>
          <div className="flex gap-2">
            <input data-testid="blog-form-cover" value={form.cover_image} onChange={e => setForm(p => ({ ...p, cover_image: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none flex-1" placeholder="https://… or click Upload" />
            <button type="button" onClick={() => coverImageInputRef.current?.click()} disabled={uploading} className="border border-[#A0743A]/40 text-[#A0743A] px-3 py-1 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors disabled:opacity-50 whitespace-nowrap" data-testid="blog-form-cover-upload">
              {uploading ? '…' : 'Upload'}
            </button>
            <input ref={coverImageInputRef} type="file" accept="image/*" onChange={handleCoverImageUpload} className="hidden" data-testid="blog-form-cover-input" />
          </div>
          {form.cover_image && (
            <img src={form.cover_image} alt="Cover preview" className="mt-3 max-h-32 object-cover border border-[#A0743A]/20" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          )}
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Tags</label>
          <div className="flex gap-2">
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none flex-1" placeholder="Add tag + Enter" />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {form.tags?.map(t => (
              <span key={t} className="bg-[#A0743A]/20 text-[#A0743A] px-2 py-1 text-[10px] uppercase tracking-wider flex items-center gap-1">
                #{t} <button type="button" onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }))} className="hover:text-red-400"><X size={10} /></button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">SEO Title</label>
          <input data-testid="blog-form-seo-title" value={form.seo_title} onChange={e => setForm(p => ({ ...p, seo_title: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="SEO page title" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">SEO Description</label>
          <input data-testid="blog-form-seo-desc" value={form.seo_description} onChange={e => setForm(p => ({ ...p, seo_description: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Meta description for search engines" />
        </div>
      </div>

      <div>
        <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Published Date</label>
        <input
          type="date"
          data-testid="blog-form-published-date"
          value={form.published_date || ''}
          onChange={e => setForm(p => ({ ...p, published_date: e.target.value }))}
          className="bg-[#1A1A1A] border border-[#A0743A]/40 text-[#FAF8F5] px-3 py-2 focus:border-[#C9A96E] focus:outline-none"
        />
        <p className="text-[#666] text-[11px] mt-2 italic">
          Leave blank to use today's date. Set to a past date if you're migrating an old post from another platform — the date you choose is what visitors will see on the blog.
        </p>
      </div>

      <div className="flex items-center gap-4 pt-4">
        <select data-testid="blog-form-status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="bg-[#1A1A1A] border border-[#A0743A]/40 text-[#FAF8F5] px-4 py-3 text-xs uppercase tracking-wider">
          <option value="draft">Save as Draft</option>
          <option value="published">Publish Now</option>
        </select>
        <button type="submit" disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="blog-form-submit">{saving ? 'Saving...' : (post ? 'Update Post' : 'Create Post')}</button>
        <button type="button" onClick={onCancel} className="border border-[#A0743A]/40 text-[#A0743A] px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors">Cancel</button>
      </div>
    </form>
  );
}

const MEDIA_CATEGORIES = ['news', 'press_release', 'interview', 'award', 'feature'];
const MEDIA_CAT_LABELS = { news: 'News', press_release: 'Press Release', interview: 'Interview', award: 'Award', feature: 'Feature' };

function MediaTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editArticle, setEditArticle] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/media`, { credentials: 'include' });
      if (res.ok) setArticles(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleSave = async (form) => {
    if (editArticle) {
      await fetch(`${API}/admin/media/${editArticle.article_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    } else {
      await fetch(`${API}/admin/media`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    }
    setShowForm(false);
    setEditArticle(null);
    fetchArticles();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media article?')) return;
    await fetch(`${API}/admin/media/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchArticles();
  };

  if (showForm || editArticle) {
    return <MediaForm article={editArticle} onSave={handleSave} onCancel={() => { setShowForm(false); setEditArticle(null); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Media Coverage ({articles.length})</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-5 py-2.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="add-media-btn"><Plus size={14} /> Add Coverage</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-media-list">
          {articles.map(a => (
            <div key={a.article_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-[#A0743A]/40 transition-colors">
              <div className="flex items-center gap-4 flex-1">
                {a.cover_image && <img src={a.cover_image} alt="" className="w-16 h-16 object-cover shrink-0 opacity-80" />}
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-[#A0743A] text-[10px] uppercase tracking-wider font-semibold">{MEDIA_CAT_LABELS[a.category] || a.category}</span>
                    <span className="text-[#555] text-[10px]">{a.published_date}</span>
                    {a.source && <span className="text-[#C9A96E] text-[10px]">{a.source}</span>}
                  </div>
                  <p className="text-[#FAF8F5]">{a.title}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditArticle(a)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors" data-testid={`edit-media-${a.article_id}`}><Pencil size={14} /></button>
                <button onClick={() => handleDelete(a.article_id)} className="border border-red-500/30 text-red-400 p-2 hover:bg-red-500/10 transition-colors" data-testid={`delete-media-${a.article_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {articles.length === 0 && <p className="text-[#999] text-center py-8">No media coverage added yet. Click "Add Coverage" to add your first article.</p>}
        </div>
      )}
    </div>
  );
}

function MediaForm({ article, onSave, onCancel }) {
  const [form, setForm] = useState(article || {
    title: '', source: '', source_url: '', summary: '', cover_image: '', published_date: new Date().toISOString().slice(0, 10), category: 'news', status: 'published'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="media-form">
      <h3 className="text-xl text-[#FAF8F5] mb-4">{article ? 'Edit Media Article' : 'Add Media Coverage'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Headline / Title *</label>
          <input data-testid="media-form-title" required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Sàwai Capital raises $10M Series A" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Source / Publication</label>
          <input data-testid="media-form-source" value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Economic Times, Mint, YourStory..." />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Link to Original Article</label>
          <input data-testid="media-form-url" value={form.source_url} onChange={e => setForm(p => ({ ...p, source_url: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="https://economictimes.com/..." />
        </div>
        <ImageUploadField
          label="Cover Image"
          value={form.cover_image}
          onChange={(url) => setForm(p => ({ ...p, cover_image: url }))}
          testid="media-form-image"
        />
      </div>
      <div>
        <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Summary / Description</label>
        <textarea data-testid="media-form-summary" value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} rows={3} className="bg-transparent border border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] p-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none" placeholder="Brief summary of the coverage..." />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Published Date</label>
          <input type="date" data-testid="media-form-date" value={form.published_date} onChange={e => setForm(p => ({ ...p, published_date: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Category</label>
          <select data-testid="media-form-category" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full">
            {MEDIA_CATEGORIES.map(c => <option key={c} value={c}>{MEDIA_CAT_LABELS[c]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Status</label>
          <select data-testid="media-form-status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>
      <div className="flex gap-4 pt-4">
        <button type="submit" disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="media-form-submit">{saving ? 'Saving...' : (article ? 'Update' : 'Add Coverage')}</button>
        <button type="button" onClick={onCancel} className="border border-[#A0743A]/40 text-[#A0743A] px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors">Cancel</button>
      </div>
    </form>
  );
}


function LeadershipTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const fetchMembers = useCallback(async () => {
    try { const res = await fetch(`${API}/admin/leadership`, { credentials: 'include' }); if (res.ok) setMembers(await res.json()); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSave = async (form) => {
    if (editMember) {
      await fetch(`${API}/admin/leadership/${editMember.member_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    } else {
      await fetch(`${API}/admin/leadership`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    }
    setShowForm(false); setEditMember(null); fetchMembers();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    await fetch(`${API}/admin/leadership/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchMembers();
  };

  if (showForm || editMember) {
    return <LeadershipForm member={editMember} onSave={handleSave} onCancel={() => { setShowForm(false); setEditMember(null); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Leadership Team ({members.length})</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-5 py-2.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="add-leader-btn"><Plus size={14} /> Add Member</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-leadership-list">
          {members.map(m => (
            <div key={m.member_id} className="border border-[#A0743A]/15 p-5 flex items-center justify-between gap-4 hover:border-[#A0743A]/40 transition-colors">
              <div className="flex items-center gap-4">
                {m.photo ? <img src={m.photo} alt="" className="w-12 h-12 object-cover shrink-0" /> : <div className="w-12 h-12 bg-[#A0743A]/10 flex items-center justify-center text-[#A0743A] text-lg shrink-0" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{m.name?.charAt(0)}</div>}
                <div>
                  <p className="text-[#FAF8F5]">{m.name}</p>
                  <p className="text-[#A0743A] text-xs">{m.title}</p>
                  <p className="text-[#555] text-[10px]">Order: {m.order}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditMember(m)} className="border border-[#A0743A]/30 text-[#A0743A] p-2 hover:bg-[#A0743A]/10 transition-colors"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(m.member_id)} className="border border-red-500/30 text-red-400 p-2 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {members.length === 0 && <p className="text-[#999] text-center py-8">No leadership profiles added yet.</p>}
        </div>
      )}
    </div>
  );
}

function LeadershipForm({ member, onSave, onCancel }) {
  const [form, setForm] = useState(member || { name: '', title: '', bio: '', photo: '', linkedin: '', twitter: '', email: '', order: 0, status: 'published' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave(form); setSaving(false); };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo is larger than 5 MB. Please compress and try again.');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/admin/upload/image`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Upload failed.');
      } else {
        const data = await res.json();
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setForm(p => ({ ...p, photo: url }));
      }
    } catch {
      alert('Network error during upload.');
    }
    setUploading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="leadership-form">
      <h3 className="text-xl text-[#FAF8F5] mb-4">{member ? 'Edit Profile' : 'Add Team Member'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Full Name *</label>
          <input required data-testid="leader-form-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Vickrant Singh Mathur" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Title / Designation *</label>
          <input required data-testid="leader-form-title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Co-Founder & CEO" />
        </div>
      </div>
      <div>
        <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Bio</label>
        <textarea data-testid="leader-form-bio" value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={4} className="bg-transparent border border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] p-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none" placeholder="Background, experience, expertise..." />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Photo</label>
          <div className="flex gap-2">
            <input data-testid="leader-form-photo" value={form.photo} onChange={e => setForm(p => ({ ...p, photo: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none flex-1" placeholder="https://… or click Upload" />
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploading} className="border border-[#A0743A]/40 text-[#A0743A] px-3 py-1 text-xs uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors disabled:opacity-50 whitespace-nowrap" data-testid="leader-form-photo-upload">
              {uploading ? '…' : 'Upload'}
            </button>
            <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="leader-form-photo-input" />
          </div>
          {form.photo && (
            <img src={form.photo} alt="Photo preview" className="mt-3 w-24 h-24 object-cover border border-[#A0743A]/20" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          )}
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">LinkedIn Profile</label>
          <input data-testid="leader-form-linkedin" value={form.linkedin} onChange={e => setForm(p => ({ ...p, linkedin: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="https://linkedin.com/in/..." />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Email</label>
          <input data-testid="leader-form-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="name@sawaicap.com" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Display Order</label>
          <input type="number" data-testid="leader-form-order" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full" />
        </div>
        <div>
          <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Status</label>
          <select data-testid="leader-form-status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>
      <div className="flex gap-4 pt-4">
        <button type="submit" disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="leader-form-submit">{saving ? 'Saving...' : (member ? 'Update' : 'Add Member')}</button>
        <button type="button" onClick={onCancel} className="border border-[#A0743A]/40 text-[#A0743A] px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors">Cancel</button>
      </div>
    </form>
  );
}





function SiteSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openGroups, setOpenGroups] = useState({ brand: true });

  useEffect(() => {
    const fetch_ = async () => {
      try { const res = await fetch(`${API}/settings`); if (res.ok) setSettings(await res.json()); } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const res = await fetch(`${API}/admin/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(settings) });
      if (res.ok) {
        setSettings(await res.json());
        invalidateSiteSettings();
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {}
    setSaving(false);
  };

  const upd = (key, val) => setSettings(p => ({ ...p, [key]: val }));
  const toggleGroup = (k) => setOpenGroups(p => ({ ...p, [k]: !p[k] }));

  const field = (label, key, placeholder = '', type = 'text') => (
    <div>
      <label className="text-[10px] tracking-wider text-[#A0743A] mb-1 block uppercase">{label}</label>
      <input data-testid={`settings-${key}`} type={type} value={settings?.[key] || ''} onChange={e => upd(key, e.target.value)} className="bg-transparent border-b border-[#A0743A]/30 text-[#FAF8F5] placeholder-[#555] py-1.5 focus:border-[#C9A96E] focus:outline-none w-full text-sm" placeholder={placeholder} />
    </div>
  );

  const textArea = (label, key, rows = 3) => (
    <div>
      <label className="text-[10px] tracking-wider text-[#A0743A] mb-1 block uppercase">{label}</label>
      <textarea data-testid={`settings-${key}`} value={settings?.[key] || ''} onChange={e => upd(key, e.target.value)} rows={rows} className="bg-transparent border border-[#A0743A]/30 text-[#FAF8F5] placeholder-[#555] p-2 focus:border-[#C9A96E] focus:outline-none w-full resize-none text-sm" />
    </div>
  );

  const Group = ({ id, title, subtitle, children }) => (
    <div className="border border-[#A0743A]/20">
      <button
        type="button"
        onClick={() => toggleGroup(id)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#A0743A]/5 transition-colors"
        data-testid={`settings-group-${id}`}
      >
        <div className="text-left">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider font-semibold">{title}</h4>
          {subtitle && <p className="text-[#666] text-xs mt-1">{subtitle}</p>}
        </div>
        {openGroups[id] ? <ChevronUp size={16} className="text-[#A0743A]" /> : <ChevronDown size={16} className="text-[#A0743A]" />}
      </button>
      {openGroups[id] && <div className="px-6 pb-6 pt-2 border-t border-[#A0743A]/15">{children}</div>}
    </div>
  );

  if (loading || !settings) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="site-settings-tab">
      <div className="flex items-center justify-between mb-6 sticky top-16 bg-[#1A1A1A] z-30 py-4 -mt-4">
        <div>
          <h3 className="text-xl text-[#FAF8F5]">Site Settings</h3>
          <p className="text-[#666] text-xs mt-1">Edit every piece of content on the landing page, navbar and footer — no code required.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-emerald-400 text-xs">Saved!</span>}
          <button onClick={handleSave} disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-6 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="save-settings-btn">{saving ? 'Saving...' : 'Save All Settings'}</button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Brand & Navbar */}
        <Group id="brand" title="Brand & Navbar" subtitle="Logo text, nav menu labels and CTA button">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Brand Name (Logo text)', 'brand_name', 'SÀWAI CAPITAL')}
            {field('Logged-out CTA', 'nav_cta_logged_out', 'Investor Login')}
            {field('Logged-in CTA', 'nav_cta_logged_in', 'Enter Dealroom')}
            {field('Nav Label: Asset Classes', 'nav_label_asset_classes', 'Asset Classes')}
            {field('Nav Label: Process', 'nav_label_process', 'Process')}
            {field('Nav Label: Investors', 'nav_label_investors', 'Investors')}
            {field('Nav Label: About', 'nav_label_about', 'About')}
            {field('Nav Label: Blog', 'nav_label_blog', 'Blog')}
            {field('Nav Label: Newsroom', 'nav_label_newsroom', 'Newsroom')}
            {field('Nav Label: Team', 'nav_label_team', 'Team')}
          </div>
        </Group>

        {/* Hero */}
        <Group id="hero" title="Hero Section" subtitle="First thing visitors see">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Eyebrow Text', 'hero_eyebrow', "India's Private Deal Network")}
            {field('Headline Line 1 (white)', 'hero_headline_1', 'Curated Access to')}
            {field('Headline Line 2 (gold)', 'hero_headline_2', 'Private Capital Markets')}
            {field('Primary Button', 'hero_cta_primary', 'Request Access')}
            {field('Secondary Button', 'hero_cta_secondary', 'Learn More')}
          </div>
          <div className="mt-4">{textArea('Subtitle', 'hero_subtitle', 2)}</div>
          <div className="mt-4">
            <ImageUploadField
              label="Background Image"
              value={settings?.hero_bg_image || ''}
              onChange={(url) => upd('hero_bg_image', url)}
              testid="settings-hero-bg-image"
              previewClass="mt-3 max-h-40 w-full object-cover border border-[#A0743A]/20"
            />
          </div>
        </Group>

        {/* Marquee */}
        <Group id="marquee" title="Marquee Strip" subtitle="Gold scrolling ticker below the hero">
          <div className="mt-4">{field('Items (comma-separated)', 'marquee_items', 'Senior Secured,Deal-Level Access,...')}</div>
        </Group>

        {/* Stats */}
        <Group id="stats" title="Stats Section" subtitle="Four animated counter cards">
          <div className="mt-4 space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="border border-[#A0743A]/15 p-4">
                <p className="text-[10px] text-[#C9A96E] uppercase tracking-wider mb-2">Stat {i}</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {field('Prefix', `stat_${i}_prefix`)}
                  {field('Value', `stat_${i}_value`)}
                  {field('Suffix', `stat_${i}_suffix`)}
                  {field('Label', `stat_${i}_label`)}
                </div>
              </div>
            ))}
          </div>
        </Group>

        {/* Asset Classes */}
        <Group id="asset_classes" title="Asset Classes Section" subtitle="Four asset class cards">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Eyebrow Text', 'asset_section_eyebrow', 'What We Offer')}
            {field('Headline Line 1', 'asset_section_headline_1', 'Four Asset Classes.')}
            {field('Headline Line 2', 'asset_section_headline_2', 'One Private Network.')}
          </div>
          <div className="mt-4">{textArea('Return Disclaimer (shown on every card)', 'asset_return_disclaimer', 2)}</div>
          <div className="mt-4 space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="border border-[#A0743A]/15 p-4">
                <p className="text-[10px] text-[#C9A96E] uppercase tracking-wider mb-2">Asset Card {i}</p>
                {field('Title', `asset_${i}_title`)}
                <div className="mt-3">{textArea('Description', `asset_${i}_desc`, 3)}</div>
                <div className="mt-3">{field('Min Ticket Line', `asset_${i}_ticket`, 'Min. ticket INR 5 crore')}</div>
              </div>
            ))}
          </div>
        </Group>

        {/* How It Works */}
        <Group id="how_it_works" title="How It Works Section" subtitle="Four numbered steps">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Eyebrow Text', 'how_section_eyebrow', 'The Process')}
            {field('Headline', 'how_section_headline', 'How It Works')}
          </div>
          <div className="mt-4">
            <ImageUploadField
              label="Background Image"
              value={settings?.how_bg_image || ''}
              onChange={(url) => upd('how_bg_image', url)}
              testid="settings-how-bg-image"
              previewClass="mt-3 max-h-40 w-full object-cover border border-[#A0743A]/20"
            />
          </div>
          <div className="mt-4 space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="border border-[#A0743A]/15 p-4">
                <p className="text-[10px] text-[#C9A96E] uppercase tracking-wider mb-2">Step {String(i).padStart(2, '0')}</p>
                {field('Title', `step_${i}_title`)}
                <div className="mt-3">{textArea('Description', `step_${i}_desc`, 2)}</div>
              </div>
            ))}
          </div>
        </Group>

        {/* Who We Serve */}
        <Group id="who_we_serve" title="Who We Serve Section" subtitle="Four investor segments">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Eyebrow Text', 'who_section_eyebrow', 'Our Investors')}
            {field('Headline', 'who_section_headline', 'Who We Serve')}
          </div>
          <div className="mt-4 space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="border border-[#A0743A]/15 p-4">
                <p className="text-[10px] text-[#C9A96E] uppercase tracking-wider mb-2">Segment {i}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {field('Title', `who_${i}_title`)}
                  {field('Criteria (small gold text)', `who_${i}_criteria`)}
                </div>
                <div className="mt-3">{textArea('Description', `who_${i}_desc`, 2)}</div>
              </div>
            ))}
          </div>
        </Group>

        {/* Philosophy / Brand Story */}
        <Group id="philosophy" title="Philosophy / Brand Story" subtitle="Quote block + our story">
          <div className="mt-4 space-y-4">
            {field('Big Quote', 'philosophy_quote', 'Private capital should not be...')}
            {field('Eyebrow Text', 'philosophy_eyebrow', 'Our Story')}
            {field('Headline', 'brand_story_headline', 'Built by Deal Originators...')}
            <ImageUploadField
              label="Background Image"
              value={settings?.philosophy_bg_image || ''}
              onChange={(url) => upd('philosophy_bg_image', url)}
              testid="settings-philosophy-bg-image"
              previewClass="mt-3 max-h-40 w-full object-cover border border-[#A0743A]/20"
            />
            {textArea('Story Paragraph 1', 'brand_story_text_1', 4)}
            {textArea('Story Paragraph 2', 'brand_story_text_2', 4)}
          </div>
        </Group>

        {/* Blog / News Section */}
        <Group id="blog" title="Blog / Insights Section" subtitle="Landing page blog preview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {field('Eyebrow Text', 'blog_section_eyebrow', 'Insights & Analysis')}
            {field('Headline', 'blog_section_headline', 'Latest from the Dealroom')}
            {field('View All Link Text', 'blog_view_all_label', 'View All Articles')}
          </div>
        </Group>

        {/* FAQ Page */}
        <Group id="faq" title="FAQ Page" subtitle="Edit the dedicated /faq page. Empty Q&A pairs are auto-hidden.">
          <div className="mt-4 space-y-4">
            {field('Eyebrow Text', 'faq_section_eyebrow', 'Frequently Asked Questions')}
            {field('Headline', 'faq_section_headline', 'Answers for Serious Investors')}
            {textArea('Subtitle', 'faq_section_subtitle', 2)}
          </div>
          <div className="mt-6 space-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <div key={i} className="border border-[#A0743A]/15 p-4">
                <p className="text-[10px] text-[#C9A96E] uppercase tracking-wider mb-2">FAQ {i}{i > 6 && <span className="text-[#666] normal-case"> (optional)</span>}</p>
                {field('Question', `faq_${i}_q`)}
                <div className="mt-3">{textArea('Answer', `faq_${i}_a`, 3)}</div>
              </div>
            ))}
          </div>
        </Group>

        {/* Terms & Conditions Page */}
        <Group id="terms" title="Terms & Conditions Page" subtitle="Edit the /terms page. Paste your full Terms in the content area below.">
          <div className="mt-4 space-y-4">
            {field('Eyebrow Text', 'terms_page_eyebrow', 'Legal')}
            {field('Page Headline', 'terms_page_headline', 'Terms & Conditions')}
            {textArea('Subtitle', 'terms_page_subtitle', 2)}
            {field('Last Updated Line', 'terms_last_updated', 'Last updated: 1 January 2026')}
            {textArea('Full Content (use blank lines between sections; numbered headings like "1. SECTION TITLE" auto-style as headings)', 'terms_content', 18)}
          </div>
        </Group>

        {/* Privacy & Cookie Policy Page */}
        <Group id="privacy" title="Privacy & Cookie Policy Page" subtitle="Edit the /privacy page. Paste your full Privacy Policy in the content area below.">
          <div className="mt-4 space-y-4">
            {field('Eyebrow Text', 'privacy_page_eyebrow', 'Legal')}
            {field('Page Headline', 'privacy_page_headline', 'Privacy & Cookie Policy')}
            {textArea('Subtitle', 'privacy_page_subtitle', 2)}
            {field('Last Updated Line', 'privacy_last_updated', 'Last updated: 1 January 2026')}
            {textArea('Full Content (use blank lines between sections; numbered headings like "1. SECTION TITLE" auto-style as headings)', 'privacy_content', 18)}
          </div>
        </Group>

        {/* Request Access Form */}
        <Group id="access" title="Request Access Form" subtitle="Landing page signup form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Eyebrow Text', 'access_section_eyebrow', 'Join the Network')}
            {field('Headline', 'access_section_headline', 'Request Access')}
          </div>
          <div className="mt-4">{textArea('Subtitle', 'access_section_subtitle', 2)}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('Success Headline', 'access_success_headline', 'Thank you.')}
            {field('Success Message', 'access_success_message', 'Our team will contact you within 48 hours.')}
            {field('WhatsApp Prompt', 'access_whatsapp_prompt', 'Prefer to speak directly?')}
          </div>
        </Group>

        {/* Contact & Footer */}
        <Group id="footer" title="Contact & Footer" subtitle="Company info, legal disclaimers">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {field('WhatsApp Number', 'whatsapp_number', '919871871888')}
            {field('Contact Email', 'contact_email', 'dealroom@sawaicap.com')}
            {field('Website URL', 'website_url', 'sawaicap.com')}
            {field('LinkedIn URL', 'linkedin_url', 'https://linkedin.com/company/...')}
            {field('Company CIN', 'company_cin')}
            {field('Company GSTIN', 'company_gstin')}
            {field('Footer "Platform" Heading', 'footer_platform_heading', 'Platform')}
            {field('Footer "Connect" Heading', 'footer_connect_heading', 'Connect')}
            {field('Footer FAQ Link Label', 'footer_faq_label', 'FAQ')}
            {field('Footer Terms Link Label', 'footer_terms_label', 'Terms & Conditions')}
            {field('Footer Privacy Link Label', 'footer_privacy_label', 'Privacy & Cookie Policy')}
          </div>
          <div className="mt-4">{field('Registered Office Address', 'company_address')}</div>
          <div className="mt-4">{textArea('Footer Tagline (left column)', 'footer_tagline', 3)}</div>
          <div className="mt-4">{textArea('Top Legal Disclaimer Bar', 'footer_legal_top', 4)}</div>
          <div className="mt-4">{textArea('Bottom Legal Disclaimer', 'footer_legal_bottom', 3)}</div>
          <div className="mt-4">{field('Copyright Line (after © year)', 'footer_copyright_prefix', 'Sàwai Capital. All rights reserved.')}</div>
        </Group>

        {/* Meta */}
        <Group id="meta" title="SEO / Meta Tags" subtitle="For Google and social sharing">
          <div className="mt-4 space-y-4">
            {field('Page Title', 'meta_title')}
            {textArea('Meta Description', 'meta_description', 2)}
          </div>
        </Group>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="save-settings-btn-bottom">{saving ? 'Saving...' : 'Save All Settings'}</button>
        {saved && <span className="text-emerald-400 text-sm">Settings saved successfully!</span>}
      </div>
    </div>
  );
}


function TeamTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'view_only' });
  const [inviting, setInviting] = useState(false);

  const roleLabels = { super_admin: 'Super Admin', admin: 'Admin', deal_manager: 'Deal Manager', content_manager: 'Content Manager', investor_relations: 'Investor Relations', view_only: 'View Only' };
  const roleColors = { super_admin: 'text-[#A0743A]', admin: 'text-[#A0743A]', deal_manager: 'text-[#C9A96E]', content_manager: 'text-emerald-400', investor_relations: 'text-blue-400', view_only: 'text-[#999]' };

  useEffect(() => {
    const fetch_ = async () => {
      try { const res = await fetch(`${API}/admin/team`, { credentials: 'include' }); if (res.ok) setMembers(await res.json()); } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await fetch(`${API}/admin/team/invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(inviteForm) });
      if (res.ok) {
        const newMember = await res.json();
        setMembers(prev => [newMember, ...prev.filter(m => m.user_id !== newMember.user_id)]);
        setShowInvite(false);
        setInviteForm({ email: '', name: '', role: 'view_only' });
      }
    } catch {}
    setInviting(false);
  };

  const updateRole = async (userId, role) => {
    const res = await fetch(`${API}/admin/team/${userId}/role`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ role }) });
    if (res.ok) { const updated = await res.json(); setMembers(prev => prev.map(m => m.user_id === userId ? updated : m)); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this team member?')) return;
    await fetch(`${API}/admin/team/${userId}`, { method: 'DELETE', credentials: 'include' });
    setMembers(prev => prev.filter(m => m.user_id !== userId));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Team Members ({members.length})</h3>
        <button onClick={() => setShowInvite(!showInvite)} className="flex items-center gap-2 bg-[#A0743A] text-[#FAF8F5] px-5 py-2.5 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors" data-testid="invite-team-btn"><Plus size={14} /> Invite Member</button>
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} className="border border-[#A0743A]/20 p-6 mb-6 space-y-4" data-testid="team-invite-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input data-testid="invite-email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none" placeholder="Email address" required />
            <input data-testid="invite-name" value={inviteForm.name} onChange={e => setInviteForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none" placeholder="Name" />
            <select data-testid="invite-role" value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none">
              {Object.entries(roleLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button type="submit" disabled={inviting} className="bg-[#A0743A] text-[#FAF8F5] px-6 py-2 text-xs uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50" data-testid="invite-submit-btn">{inviting ? 'Adding...' : 'Add Team Member'}</button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-team-list">
          {members.map(m => (
            <div key={m.user_id} className="border border-[#A0743A]/15 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <Shield size={14} className={roleColors[m.role] || 'text-[#999]'} />
                  <span className={`text-xs uppercase tracking-wider font-semibold ${roleColors[m.role] || 'text-[#999]'}`}>{roleLabels[m.role] || m.role}</span>
                </div>
                <p className="text-[#FAF8F5]">{m.name}</p>
                <p className="text-[#999] text-xs">{m.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={m.role} onChange={e => updateRole(m.user_id, e.target.value)} className="bg-[#1A1A1A] border border-[#A0743A]/30 text-[#FAF8F5] px-3 py-1.5 text-xs" data-testid={`team-role-${m.user_id}`}>
                  {Object.entries(roleLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  <option value="investor">Remove (Investor)</option>
                </select>
                <button onClick={() => removeMember(m.user_id)} className="border border-red-500/30 text-red-400 p-2 hover:bg-red-500/10 transition-colors" data-testid={`remove-team-${m.user_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {members.length === 0 && <p className="text-[#999] text-center py-8">No team members yet.</p>}
        </div>
      )}
    </div>
  );
}


function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/analytics?days=${days}`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, [days]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const maxDaily = Math.max(...(data.daily_views?.map(d => d.views) || [1]), 1);

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-[#FAF8F5]">Website Analytics</h3>
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)} className={`px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${days === d ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A]/30 text-[#A0743A] hover:bg-[#A0743A]/10'}`} data-testid={`analytics-days-${d}`}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="border border-[#A0743A]/20 p-5">
          <TrendingUp size={18} className="text-[#A0743A] mb-2" strokeWidth={1.25} />
          <p className="text-2xl text-[#FAF8F5] font-semibold">{data.today_views}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">Today's Views</p>
          <p className="text-[#555] text-[10px] mt-1">Yesterday: {data.yesterday_views}</p>
        </div>
        <div className="border border-[#A0743A]/20 p-5">
          <Eye size={18} className="text-[#A0743A] mb-2" strokeWidth={1.25} />
          <p className="text-2xl text-[#FAF8F5] font-semibold">{data.total_views}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">Total Views ({days}d)</p>
        </div>
        <div className="border border-[#A0743A]/20 p-5">
          <Users size={18} className="text-[#A0743A] mb-2" strokeWidth={1.25} />
          <p className="text-2xl text-[#FAF8F5] font-semibold">{data.unique_visitors}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">Unique Visitors</p>
        </div>
        <div className="border border-[#A0743A]/20 p-5">
          <BarChart3 size={18} className="text-[#A0743A] mb-2" strokeWidth={1.25} />
          <p className="text-2xl text-[#FAF8F5] font-semibold">{data.conversion_rate}%</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">Conversion Rate</p>
          <p className="text-[#555] text-[10px] mt-1">{data.total_access_requests_period} requests</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Daily Views Chart */}
        <div className="lg:col-span-2 border border-[#A0743A]/20 p-6">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">Daily Page Views</h4>
          <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
            {data.daily_views?.slice(-14).map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.views} views`}>
                <span className="text-[#999] text-[8px] opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
                <div className="w-full bg-[#A0743A]/80 hover:bg-[#A0743A] transition-colors rounded-t-sm" style={{ height: `${Math.max(4, (d.views / maxDaily) * 130)}px` }} />
                <span className="text-[#555] text-[7px] rotate-[-45deg] w-8 text-center">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="border border-[#A0743A]/20 p-6">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">Devices</h4>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Monitor size={16} className="text-[#A0743A]" />
              <div className="flex-1">
                <div className="flex justify-between mb-1"><span className="text-[#FAF8F5] text-sm">Desktop</span><span className="text-[#999] text-sm">{data.device_breakdown.desktop}</span></div>
                <div className="h-1.5 bg-[#1A1A1A] border border-[#A0743A]/20"><div className="h-full bg-[#A0743A]" style={{ width: `${(data.device_breakdown.desktop / Math.max(data.total_views, 1)) * 100}%` }} /></div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Smartphone size={16} className="text-[#C9A96E]" />
              <div className="flex-1">
                <div className="flex justify-between mb-1"><span className="text-[#FAF8F5] text-sm">Mobile</span><span className="text-[#999] text-sm">{data.device_breakdown.mobile}</span></div>
                <div className="h-1.5 bg-[#1A1A1A] border border-[#A0743A]/20"><div className="h-full bg-[#C9A96E]" style={{ width: `${(data.device_breakdown.mobile / Math.max(data.total_views, 1)) * 100}%` }} /></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Pages */}
      <div className="border border-[#A0743A]/20 p-6">
        <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">Top Pages</h4>
        <div className="space-y-2" data-testid="top-pages">
          {data.top_pages?.map((p, i) => (
            <div key={p.path} className="flex items-center justify-between py-2 border-b border-[#A0743A]/10 last:border-0">
              <div className="flex items-center gap-3">
                <span className="text-[#A0743A] text-xs w-6">{i + 1}.</span>
                <span className="text-[#FAF8F5] text-sm">{p.path === '/' ? 'Home / Landing Page' : p.path}</span>
              </div>
              <span className="text-[#999] text-sm">{p.views} views</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InvestorCRMView({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/admin/investors/${userId}`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, [userId]);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-[#999]">Investor not found.</p>;

  const { investor, ndas, im_requests: ims, watchlist, deals_map, summary } = data;
  const getDealName = (dealId) => deals_map[dealId]?.deal_name || dealId;

  return (
    <div data-testid="investor-crm-view">
      <button onClick={onBack} className="flex items-center gap-2 text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors mb-6" data-testid="crm-back-btn"><ArrowLeft size={16} /> Back to Investors</button>

      {/* Profile Header */}
      <div className="border border-[#A0743A]/20 p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div>
            <h3 className="text-2xl text-[#FAF8F5] mb-1">{investor.name}</h3>
            <p className="text-[#999] text-sm mb-4">{investor.email}</p>
            <div className="flex flex-wrap gap-4 text-sm">
              {investor.entity_name && <span className="text-[#C9A96E]">{investor.entity_name}</span>}
              {investor.aum_range && <span className="text-[#999]">AUM: {investor.aum_range}</span>}
              {investor.city && <span className="text-[#999]">{investor.city}</span>}
              {investor.phone && <span className="text-[#999]">{investor.phone}</span>}
            </div>
            {investor.bio && <p className="text-[#555] text-sm mt-3 italic">{investor.bio}</p>}
          </div>
          <div className="flex gap-3">
            <span className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border ${investor.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'}`}>{investor.status}</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-[#A0743A]/20 p-4 text-center">
          <p className="text-xl text-[#A0743A] font-semibold">{summary.total_ndas}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">NDAs Signed</p>
        </div>
        <div className="border border-[#A0743A]/20 p-4 text-center">
          <p className="text-xl text-[#A0743A] font-semibold">{summary.total_im_requests}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">IM Requests</p>
        </div>
        <div className="border border-[#A0743A]/20 p-4 text-center">
          <p className="text-xl text-[#A0743A] font-semibold">{summary.watchlist_count}</p>
          <p className="text-[#999] text-xs uppercase tracking-wider">Watchlist</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NDAs */}
        <div className="border border-[#A0743A]/20 p-6">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">Signed NDAs ({ndas.length})</h4>
          {ndas.length === 0 ? <p className="text-[#555] text-sm">None</p> : (
            <div className="space-y-3">
              {ndas.map(n => (
                <div key={n.nda_id} className="flex justify-between items-center py-2 border-b border-[#A0743A]/10 last:border-0">
                  <span className="text-[#FAF8F5] text-sm">{getDealName(n.deal_id)}</span>
                  <span className="text-[#999] text-xs">{n.signed_at ? new Date(n.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* IM Requests */}
        <div className="border border-[#A0743A]/20 p-6">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">IM Requests ({ims.length})</h4>
          {ims.length === 0 ? <p className="text-[#555] text-sm">None</p> : (
            <div className="space-y-3">
              {ims.map(im => (
                <div key={im.request_id} className="py-2 border-b border-[#A0743A]/10 last:border-0">
                  <div className="flex justify-between items-center">
                    <span className="text-[#FAF8F5] text-sm">{getDealName(im.deal_id)}</span>
                    <span className={`px-2 py-0.5 text-[9px] uppercase tracking-wider font-semibold border ${im.status === 'approved' ? 'text-emerald-400 border-emerald-500/30' : im.status === 'rejected' ? 'text-red-400 border-red-500/30' : 'text-amber-400 border-amber-500/30'}`}>{im.status}</span>
                  </div>
                  {im.message && <p className="text-[#555] text-xs mt-1 italic">"{im.message}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Watchlist */}
        <div className="border border-[#A0743A]/20 p-6 lg:col-span-2">
          <h4 className="text-sm text-[#C9A96E] uppercase tracking-wider mb-4">Watchlist ({watchlist.length})</h4>
          {watchlist.length === 0 ? <p className="text-[#555] text-sm">Empty</p> : (
            <div className="flex flex-wrap gap-3">
              {watchlist.map(w => (
                <span key={w.watchlist_id} className="border border-[#A0743A]/20 px-4 py-2 text-[#FAF8F5] text-sm">{getDealName(w.deal_id)}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExportBar() {
  const collections = [
    { key: 'investors', label: 'Investors' },
    { key: 'deals', label: 'Deals' },
    { key: 'im-requests', label: 'IM Requests' },
    { key: 'access-requests', label: 'Access Requests' },
    { key: 'ndas', label: 'NDAs' },
    { key: 'blogs-published', label: 'Blogs (Published)' },
    { key: 'blogs', label: 'Blogs (All)' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6 p-4 border border-[#A0743A]/15" data-testid="export-bar">
      <Download size={14} className="text-[#A0743A]" />
      <span className="text-[#999] text-xs uppercase tracking-wider mr-2">Export CSV:</span>
      {collections.map(c => (
        <a key={c.key} href={`${API}/admin/export/${c.key}`} target="_blank" rel="noopener noreferrer" className="border border-[#A0743A]/30 text-[#A0743A] px-3 py-1 text-[10px] uppercase tracking-wider hover:bg-[#A0743A]/10 transition-colors" data-testid={`export-${c.key}`}>{c.label}</a>
      ))}
    </div>
  );
}


export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('deals');
  const [stats, setStats] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/admin/stats`, { credentials: 'include' });
        if (res.ok) setStats(await res.json());
      } catch {}
    };
    const fetchPerms = async () => {
      try {
        const res = await fetch(`${API}/admin/permissions`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPermissions(data.permissions || []);
        }
      } catch {}
    };
    fetchStats();
    fetchPerms();
  }, []);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const allTabs = [
    { key: 'analytics', label: 'Analytics', icon: TrendingUp, perm: 'stats' },
    { key: 'deals', label: 'Deals', icon: FileText, perm: 'deals' },
    { key: 'investors', label: 'Investors', icon: Users, perm: 'investors' },
    { key: 'ndas', label: 'NDAs', icon: FileCheck, perm: 'ndas' },
    { key: 'im-requests', label: 'IM Requests', icon: Inbox, perm: 'im_requests' },
    { key: 'access-requests', label: 'Access Requests', icon: Inbox, perm: 'access_requests' },
    { key: 'blog', label: 'Blog', icon: BookOpen, perm: 'blog' },
    { key: 'media', label: 'Media', icon: Newspaper, perm: 'blog' },
    { key: 'leadership', label: 'Leadership', icon: Crown, perm: 'blog' },
    { key: 'team', label: 'Team', icon: UserCog, perm: 'team' },
    { key: 'activity', label: 'Activity', icon: Bell, perm: 'activity' },
    { key: 'settings', label: 'Settings', icon: BarChart3, perm: 'blog' },
  ];
  const tabs = allTabs.filter(t => permissions.includes(t.perm));

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="admin-panel">
      {/* Header */}
      <header className="border-b border-[#A0743A]/20 bg-[#1A1A1A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="text-[#A0743A] text-lg tracking-[0.15em]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>SÀWAI CAPITAL</Link>
          <div className="flex items-center gap-6">
            <span className="text-[#C9A96E] text-xs uppercase tracking-wider">Admin</span>
            <Link to="/portal" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-portal">Portal</Link>
            <div className="flex items-center gap-3 pl-6 border-l border-[#A0743A]/20">
              <span className="text-[#999] text-xs hidden sm:block">{user?.name}</span>
              <button onClick={handleLogout} className="text-[#999] hover:text-[#FAF8F5] transition-colors" data-testid="admin-logout-btn"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-10">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-2">Administration</p>
          <h1 className="text-3xl text-[#FAF8F5] tracking-tight">Dealroom Dashboard</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
            <StatCard icon={BarChart3} label="Total Deals" value={stats.total_deals} sub={`${stats.open_deals} open`} />
            <StatCard icon={Users} label="Investors" value={stats.total_investors} sub={`${stats.approved_investors} approved`} />
            <StatCard icon={FileCheck} label="NDAs Signed" value={stats.total_ndas || 0} />
            <StatCard icon={Inbox} label="IM Requests" value={stats.total_im_requests} sub={`${stats.pending_im_requests} pending`} />
            <StatCard icon={FileText} label="Access Requests" value={stats.total_access_requests} sub={`${stats.pending_access_requests} pending`} />
          </div>
        )}

        {/* Export Bar */}
        <ExportBar />

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#A0743A]/20 mb-8 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              data-testid={`admin-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 text-xs uppercase tracking-wider transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-[#A0743A] text-[#A0743A]'
                  : 'border-transparent text-[#999] hover:text-[#FAF8F5]'
              }`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'deals' && <DealsTab />}
        {activeTab === 'investors' && <InvestorsTab />}
        {activeTab === 'ndas' && <NDAsTab />}
        {activeTab === 'im-requests' && <IMRequestsTab />}
        {activeTab === 'access-requests' && <AccessRequestsTab />}
        {activeTab === 'blog' && <BlogTab />}
        {activeTab === 'media' && <MediaTab />}
        {activeTab === 'leadership' && <LeadershipTab />}
        {activeTab === 'team' && <TeamTab />}
        {activeTab === 'activity' && <ActivityTab />}
        {activeTab === 'settings' && <SiteSettingsTab />}
      </main>
    </div>
  );
}
